import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { useLenis } from "lenis/react";
import { NAV_LINKS, CONTACT_DETAILS } from "@/data/content";
import { Logo } from "@/components/Logo";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const lenis = useLenis();
  const panelRef = useRef(null);

  useEffect(() => setOpen(false), [location.pathname]);

  /* While the phone menu is open the page behind must not move: pause Lenis, hide overflow and
     swallow touch-move unless the panel itself needs to scroll (short phones). */
  useEffect(() => {
    if (!open) return;
    lenis?.stop();
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = "hidden";
    const blockTouch = (e) => {
      const panel = panelRef.current;
      const panelScrolls = panel && panel.scrollHeight > panel.clientHeight;
      if (!(panelScrolls && panel.contains(e.target))) e.preventDefault();
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("touchmove", blockTouch, { passive: false });
    document.addEventListener("keydown", onKey);
    return () => {
      html.style.overflow = prevOverflow;
      document.removeEventListener("touchmove", blockTouch);
      document.removeEventListener("keydown", onKey);
      lenis?.start();
    };
  }, [open, lenis]);

  const linkClass = ({ isActive }) =>
    `relative px-1 py-2 text-[13px] font-medium tracking-wide transition-colors duration-200 whitespace-nowrap ${
      isActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900"
    } after:absolute after:left-0 after:bottom-0 after:h-0.5 after:bg-rosegold-metallic after:transition-all after:duration-300 ${
      isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-blush/95 border-b border-slate-200/80 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[76px] sm:h-[112px]">
          <Logo />

          <nav className="hidden xl:flex items-center gap-5 2xl:gap-7" data-testid="desktop-nav">
            {NAV_LINKS.filter((l) => l.id !== "contact").map((link) => (
              <NavLink
                key={link.id}
                to={link.path}
                end={link.path === "/"}
                className={linkClass}
                data-testid={`nav-link-${link.id}`}
              >
                {link.name}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/contact" data-testid="nav-quote-cta-btn" className="btn btn-primary btn-sm">
              <span className="sm:hidden">Free Quote</span>
              <span className="hidden sm:inline">Get Your Free Quote</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(!open)}
              className="xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-nav"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <nav
          id="mobile-nav"
          ref={panelRef}
          data-testid="mobile-nav"
          data-lenis-prevent
          className="menu-enter xl:hidden fixed inset-x-0 top-[76px] sm:top-[112px] bottom-0 z-40 bg-blush border-t border-slate-100 overflow-y-auto overscroll-contain"
        >
          <div className="px-4 py-4 flex flex-col gap-1 min-h-full">
            {NAV_LINKS.map((link, i) => (
              <div key={link.id} className="menu-item-enter" style={{ animationDelay: `${0.03 * i}s` }}>
                <NavLink
                  to={link.path}
                  end={link.path === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${link.id}`}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                      isActive
                        ? "bg-emerald-50 text-slate-900"
                        : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              </div>
            ))}
            <div className="mt-3 px-4 py-4 rounded-md bg-emerald-100 text-sm text-slate-600 flex items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600 mb-1">Call or WhatsApp</p>
                <a href={CONTACT_DETAILS.phoneHref} data-testid="mobile-nav-phone" className="inline-flex items-center min-h-[44px] font-heading font-semibold text-slate-900 hover:text-rosegold-deep transition-colors">{CONTACT_DETAILS.phone}</a>
              </div>
              <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-nav-quote-btn" className="btn btn-secondary btn-sm shrink-0">
                Get a Quote <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
